<script setup>
/**
 * THE ONE SWITCH MODEL (ease package P-5.1): switches save the moment they are flipped; typed
 * text saves with the page's bar.
 *
 * There were three models for this one setting: instant on Overview and in the chat panel, a
 * draft behind Personality's Save bar, and a Save button per permission card. An owner who
 * flipped it on Personality and left had changed nothing. This is the Overview's switch,
 * lifted out so every page has the same one: it writes `{ aiEnabled }` alone, renders the
 * ANSWER (never the click), and remounts after a failed write so it cannot sit in a position
 * the account is not in (`Switch` flips its own copy on click whether or not we agree).
 */
import { ref } from 'vue';

import Banner from 'dashboard/components-next/banner/Banner.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

import { useWasloI18n } from '../i18n.js';
import { wasloApi } from '../api/client.js';

const props = defineProps({
  /** What the account says now. The parent owns it; this only reports the saved answer. */
  modelValue: { type: Boolean, default: false },
  accountId: { type: Number, default: null },
  /** Leave the On/Off words out, for a page that already says them in a sentence. */
  compact: { type: Boolean, default: false },
});

const emit = defineEmits(['update:modelValue', 'saved']);

const { t } = useWasloI18n();

const isSaving = ref(false);
const error = ref('');
const epoch = ref(0);

const onToggle = async next => {
  if (isSaving.value || props.accountId === null) {
    epoch.value += 1;
    return;
  }
  isSaving.value = true;
  error.value = '';
  try {
    const config = await wasloApi.updateConfig(props.accountId, {
      aiEnabled: next,
    });
    emit('update:modelValue', config.aiEnabled === true);
    emit('saved', config);
  } catch {
    error.value = t('WASLO.OVERVIEW.REPLIES.SAVE_FAILED');
    epoch.value += 1;
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col items-end gap-1">
    <div class="flex items-center gap-2">
      <span v-if="!compact" class="text-sm text-n-slate-11" data-waslo-ai-state>
        {{
          modelValue
            ? t('WASLO.OVERVIEW.REPLIES.ON')
            : t('WASLO.OVERVIEW.REPLIES.OFF')
        }}
      </span>
      <Switch
        :key="epoch"
        :model-value="modelValue"
        :aria-label="t('WASLO.OVERVIEW.REPLIES.HEADING')"
        data-waslo-ai-switch
        @update:model-value="onToggle"
      />
    </div>
    <p
      v-if="isSaving"
      class="mb-0 text-sm text-n-slate-11"
      data-waslo-ai-saving
    >
      {{ t('WASLO.OVERVIEW.REPLIES.SAVING') }}
    </p>
    <Banner v-else-if="error" color="ruby" data-waslo-ai-error>
      {{ error }}
    </Banner>
  </div>
</template>
